import React from 'react';
import { 
  Shield, User, Activity, Brain, Share2, Lock, 
  Baby, RefreshCw, Mail, Phone, FileText 
} from 'lucide-react';
import PolicySectionCard from '@/components/policy/PolicySectionCard';
import PageHeader from '@/components/layout/PageHeader';
import PolicyLayout from '@/components/policy/PolicyLayout';
import { motion } from 'framer-motion';

const PrivacyPolicy = () => {
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const item = {
    hidden: { y: 20, opacity: 0 },
    show: { y: 0, opacity: 1 }
  };

  return (
    <PolicyLayout>
      <PageHeader
        title="Privacy Policy"
        description="Last updated: April 11, 2025"
        icon={<Shield className="w-8 h-8 text-primary" />}
      />

      <motion.div 
        className="space-y-6" 
        variants={container}
        initial="hidden"
        animate="show"
      >
        {/* Introduction */}
        <motion.div variants={item}>
          <PolicySectionCard title="Introduction" icon={FileText} variant="blue">
            <p>
              At Edaptica, a product of Cibeto Technologies Private Limited, we are committed to safeguarding the privacy and personal data of our users. This Privacy Policy outlines the types of information we collect, how we use it, and your rights regarding that information.
            </p>
            <p className="mt-4">
              By accessing or using the Edaptica platform, you agree to the practices described in this Privacy Policy. If you do not agree with any part of this policy, please refrain from using our platform.
            </p>
          </PolicySectionCard>
        </motion.div>

        {/* Information We Collect */}
        <motion.div variants={item}>
          <PolicySectionCard title="Information We Collect" icon={User} variant="purple">
            <div className="space-y-4">
              <h3 className="font-semibold">2.1 Personal Information</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Full name, email address, phone number, and contact details</li>
                <li>Profile details (educational background, learning goals, interests)</li>
                <li>Payment information for subscription services (via secure third-party gateways)</li>
                <li>Any communication or feedback shared with our support team</li>
              </ul>
              <p className="mt-4">You may provide this information when you:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Register or create an account</li>
                <li>Participate in quizzes, learning modules, or surveys</li>
                <li>Subscribe to newsletters</li>
                <li>Contact our support team</li>
              </ul>
              <div className="mt-4 p-4 bg-purple-50 rounded-lg border border-purple-100">
                <p className="text-purple-800">⚠️ Important: Edaptica is intended for users aged 13 and above. We do not knowingly collect personal information from children under the age of 13 without verifiable parental consent.</p>
              </div>
              <h3 className="font-semibold mt-6">2.2 Automatically Collected Usage Data</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>IP address, browser type, device model, and operating system</li>
                <li>Pages visited, duration of sessions, and navigation flow</li>
                <li>Learning activity data such as quiz scores, flashcard usage, progress tracking, and engagement metrics</li>
                <li>Referring/exit pages and date/time stamps</li>
              </ul>
            </div>
          </PolicySectionCard>
        </motion.div>

        {/* How We Use Information */}
        <motion.div variants={item}>
          <PolicySectionCard title="How We Use Information" icon={Brain} variant="teal">
            <ul className="list-disc pl-6 space-y-2">
              <li>Provide and optimize our services, features, and personalized content</li>
              <li>Customize learning paths based on user preferences, behavior, and goals</li>
              <li>Improve our AI-driven recommendations through aggregated analysis</li>
              <li>Process payments securely for subscriptions</li>
              <li>Send notifications, updates, or critical announcements related to your account</li>
              <li>Offer customer support and resolve technical issues</li>
              <li>Prevent misuse or fraudulent activities</li>
              <li>Conduct research and development, using anonymized data to enhance Edaptica's adaptive algorithms and product roadmap</li>
            </ul>
          </PolicySectionCard>
        </motion.div>

        {/* Information Sharing */}
        <motion.div variants={item}>
          <PolicySectionCard title="Information Sharing" icon={Share2} variant="rose">
            <div className="space-y-4">
              <p>We do not sell your personal data to third parties. However, we may share your information with:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Authorized Service Providers: Cloud hosting, analytics platforms (e.g., Google Analytics), payment gateways, and email services.</li>
                <li>Institutional Partners: If part of a school or enterprise account, data may be shared with admins or educators.</li>
                <li>Legal Authorities: When required by law or legal process.</li>
                <li>Business Transfers: During mergers or acquisitions.</li>
              </ul>
              <p>All service providers are contractually obligated to safeguard your data.</p>
            </div>
          </PolicySectionCard>
        </motion.div>

        {/* Data Security */}
        <motion.div variants={item}>
          <PolicySectionCard title="Data Security" icon={Lock} variant="amber">
            <ul className="list-disc pl-6 space-y-2">
              <li>SSL encryption</li>
              <li>Firewalled hosting environments</li>
              <li>Secure access protocols</li>
              <li>Regular security audits</li>
            </ul>
            <p className="mt-4">We encourage users to use strong passwords and report any suspicious activity.</p>
          </PolicySectionCard>
        </motion.div>

        {/* User Rights */}
        <motion.div variants={item}>
          <PolicySectionCard title="Your Rights" icon={Lock} variant="indigo">
            <div className="space-y-4">
              <p>You have the right to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Access and update your data</li>
                <li>Delete your data</li>
                <li>Restrict or object to processing</li>
                <li>Request data portability</li>
                <li>Withdraw consent</li>
              </ul>
              <p className="mt-4">Contact us at 📧 privacy@edaptica.com to exercise your rights.</p>
            </div>
          </PolicySectionCard>
        </motion.div>

        {/* Children's Privacy */}
        <motion.div variants={item}>
          <PolicySectionCard title="Children's Privacy" icon={Baby} variant="cyan">
            <p>
              Edaptica is not intended for children under 13 (or your local minimum legal age). We do not knowingly collect data from children without verified parental consent.
            </p>
            <p className="mt-4">If we inadvertently collected such data, please contact us at privacy@edaptica.com.</p>
          </PolicySectionCard>
        </motion.div>

        {/* Account & Data Deletion */}
        <motion.div variants={item}>
          <PolicySectionCard title="Account & Data Deletion" icon={User} variant="amber">
            <div className="space-y-4">
              <p>If you wish to delete your Edaptica account and data, submit a request at:</p>
              <p>🔗 <a href="https://edaptica.com/delete-account" className="text-red-600 underline">https://edaptica.com/delete-account</a></p>
              <p>This removes your profile, activity, and subscription history. Some billing data may be retained for up to 180 days as required by law.</p>
              <p>We do not currently support partial data deletion. For help, contact <strong>privacy@edaptica.com</strong>.</p>
            </div>
          </PolicySectionCard>
        </motion.div>

        {/* Policy Updates */}
        <motion.div variants={item}>
          <PolicySectionCard title="Policy Updates" icon={RefreshCw} variant="emerald">
            <p>
              We may revise this Privacy Policy periodically. Changes will be posted here with an updated date.
            </p>
            <p className="mt-4">Major changes will be communicated via in-app notifications or email.</p>
          </PolicySectionCard>
        </motion.div>

        {/* Contact Us */}
        <motion.div variants={item}>
          <PolicySectionCard title="Contact Us" icon={Mail} variant="blue">
            <div className="space-y-4">
              <p>If you have questions or concerns about this policy, contact us:</p>
              <div className="flex flex-col space-y-2">
                <div className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-blue-600" />
                  <span>support@edaptica.com</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-5 h-5 text-blue-600" />
                  <span>+91-7992096907</span>
                </div>
              </div>
            </div>
          </PolicySectionCard>
        </motion.div>
      </motion.div>
    </PolicyLayout>
  );
};

export default PrivacyPolicy;
